import { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatNumber, progressColor, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowLeft, Loader2, Pencil, Check, X, Plus, Trash2, Sparkles, Layers3, RotateCcw, ListChecks } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import DereAnoView from "@/pages/DereAnoView";

const UNIT_OPTIONS = ["m", "m²", "m³", "adet", "kg", "ton", "lt"];

function ProgressBar({ pct }) {
  const color = progressColor(pct);
  return (
    <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
      <div style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }} className="h-full" />
    </div>
  );
}

function PlannedCell({ itemKey, subKey, groupKey, planned, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(planned));
  const [saving, setSaving] = useState(false);

  const start = () => { setValue(String(planned)); setEditing(true); };
  const cancel = () => setEditing(false);

  const save = async () => {
    const n = parseFloat(value);
    if (isNaN(n) || n < 0) { toast.error("Geçerli bir sayı girin"); return; }
    setSaving(true);
    try {
      const payload = { item_key: itemKey, sub_item_key: subKey, planned: n };
      if (groupKey) payload.group_key = groupKey;
      await api.put("/items/planned", payload);
      toast.success("Planlanan güncellendi");
      setEditing(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  if (!editing) {
    return (
      <div className="flex items-center justify-end gap-2">
        <span className="font-mono-num text-slate-900" data-testid={`planned-value-${subKey}${groupKey ? "-" + groupKey : ""}`}>{formatNumber(planned)}</span>
        {canEdit && (
          <button type="button" onClick={start} className="opacity-40 hover:opacity-100 text-sky-600 transition-opacity" aria-label="Düzenle" data-testid={`planned-edit-${subKey}${groupKey ? "-" + groupKey : ""}`}>
            <Pencil size={13} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-end gap-1">
      <Input type="number" step="0.01" min="0" value={value} onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") cancel(); }}
        autoFocus className="w-28 h-8 text-right font-mono-num border-2 border-sky-400" data-testid={`planned-input-${subKey}${groupKey ? "-" + groupKey : ""}`} />
      <Button size="icon" variant="ghost" className="h-8 w-8 text-green-700 hover:bg-green-50" onClick={save} disabled={saving}>
        {saving ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />}
      </Button>
      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-700 hover:bg-red-50" onClick={cancel} disabled={saving}>
        <X size={14} />
      </Button>
    </div>
  );
}

function SubPursantajCell({ itemKey, subKey, pursantaj, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(pursantaj || 0));
  const [saving, setSaving] = useState(false);

  const start = () => { setValue(String(pursantaj || 0)); setEditing(true); };
  const cancel = () => setEditing(false);

  const save = async () => {
    const n = parseFloat(value);
    if (isNaN(n) || n < 0 || n > 100) { toast.error("Pursantaj 0-100 arası olmalı"); return; }
    setSaving(true);
    try {
      await api.put("/items/sub-pursantaj", { item_key: itemKey, sub_item_key: subKey, pursantaj: n });
      toast.success("Alt pursantaj güncellendi (tüm gruplara uygulandı)");
      setEditing(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  if (!editing) {
    return (
      <div className="flex items-center justify-end gap-1.5">
        <span
          className="inline-flex items-center gap-1 font-mono-num font-bold text-xs bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-full"
          data-testid={`sub-pursantaj-${subKey}`}
          title="Bu alt kırılımın ağırlığı — tüm gruplarda aynıdır"
        >
          %{(parseFloat(pursantaj) || 0).toFixed(1)}
        </span>
        {canEdit && (
          <button type="button" onClick={start} className="opacity-40 hover:opacity-100 text-amber-700 transition-opacity" aria-label="Düzenle" data-testid={`sub-pursantaj-edit-${subKey}`}>
            <Pencil size={13} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-end gap-1">
      <Input type="number" step="0.1" min="0" max="100" value={value} onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") cancel(); }}
        autoFocus className="w-20 h-8 text-right font-mono-num border-2 border-amber-400" data-testid={`sub-pursantaj-input-${subKey}`} />
      <Button size="icon" variant="ghost" className="h-8 w-8 text-green-700 hover:bg-green-50" onClick={save} disabled={saving}>
        {saving ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />}
      </Button>
      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-700 hover:bg-red-50" onClick={cancel} disabled={saving}>
        <X size={14} />
      </Button>
    </div>
  );
}

function DereStagePursantajPanel({ initial, totalPursantaj, isAdmin, onSaved }) {
  const [vals, setVals] = useState({
    kazi: String(initial.kazi ?? 0),
    radye: String(initial.radye ?? 0),
    perde: String(initial.perde ?? 0),
    tabliye: String(initial.tabliye ?? 0),
  });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setVals({
      kazi: String(initial.kazi ?? 0),
      radye: String(initial.radye ?? 0),
      perde: String(initial.perde ?? 0),
      tabliye: String(initial.tabliye ?? 0),
    });
    setDirty(false);
  }, [initial.kazi, initial.radye, initial.perde, initial.tabliye]);

  const setField = (k, v) => { setVals((prev) => ({ ...prev, [k]: v })); setDirty(true); };

  const save = async () => {
    const parsed = {};
    for (const k of ["kazi", "radye", "perde", "tabliye"]) {
      const n = parseFloat(vals[k]);
      if (isNaN(n) || n < 0 || n > 100) { toast.error(`${k} pursantajı 0-100 arası olmalı`); return; }
      parsed[k] = n;
    }
    setSaving(true);
    try {
      await api.put("/dere/stage-pursantaj", parsed);
      toast.success("Aşama pursantajları güncellendi (tüm Dere'lere uygulandı)");
      setDirty(false);
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const stageDefs = [
    { key: "kazi", label: "Kazı", dot: "bg-amber-500", bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-800" },
    { key: "radye", label: "Radye", dot: "bg-blue-500", bg: "bg-blue-50", border: "border-blue-300", text: "text-blue-800" },
    { key: "perde", label: "Perde", dot: "bg-purple-500", bg: "bg-purple-50", border: "border-purple-300", text: "text-purple-800" },
    { key: "tabliye", label: "Tabliye", dot: "bg-teal-500", bg: "bg-teal-50", border: "border-teal-300", text: "text-teal-800" },
  ];

  const total = stageDefs.reduce((s, sd) => s + (parseFloat(vals[sd.key]) || 0), 0);

  return (
    <div className="site-card p-6 rounded-lg" data-testid="dere-stage-pursantaj-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight font-display">
            Aşama Pursantajları
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Her aşamanın (Kazı / Radye / Perde / Tabliye) genel ilerlemedeki ağırlığı. Tüm Dere kalemlerinde ortak kullanılır.
            {totalPursantaj > 0 && (
              <> <span className="text-slate-400">·</span> <b>Toplam</b> (Aşama + Ek Alt Kırılım): <span className="font-mono-num font-bold text-amber-700">%{totalPursantaj.toFixed(1)}</span></>
            )}
          </p>
        </div>
        <div className={`px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-widest border ${total > 0 ? "bg-amber-50 text-amber-800 border-amber-300" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
          Aşama Toplamı: %{total.toFixed(1)}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {stageDefs.map((sd) => (
          <div key={sd.key} className={`${sd.bg} ${sd.border} border rounded-md p-3`}>
            <div className="flex items-center gap-2 mb-2">
              <span className={`w-2 h-2 rounded-full ${sd.dot}`} />
              <span className={`text-[11px] font-black uppercase tracking-widest ${sd.text}`}>{sd.label}</span>
            </div>
            <div className="flex items-center gap-1">
              <Input
                type="number" step="0.1" min="0" max="100"
                value={vals[sd.key]}
                onChange={(e) => setField(sd.key, e.target.value)}
                disabled={!isAdmin}
                className={`h-10 font-mono-num font-black text-lg text-right border-2 ${isAdmin ? "border-slate-300 focus:border-amber-400" : "border-transparent bg-transparent"}`}
                data-testid={`stage-purs-input-${sd.key}`}
              />
              <span className="text-slate-500 font-bold">%</span>
            </div>
          </div>
        ))}
      </div>
      {isAdmin && (
        <div className="mt-4 flex justify-end">
          <Button
            className="bg-amber-500 hover:bg-amber-600 text-white font-bold h-10"
            onClick={save}
            disabled={saving || !dirty}
            data-testid="stage-purs-save"
          >
            {saving ? <Loader2 className="animate-spin" size={16} /> : (dirty ? "Değişiklikleri Kaydet" : "Kaydedildi")}
          </Button>
        </div>
      )}
    </div>
  );
}

function SubItemsTable({ item, subs, groupKey, isAdmin, load, onEditSub, onDeleteSub, onOpenEntries }) {
  const totalSubPurs = subs.reduce((sum, s) => sum + (parseFloat(s.sub_pursantaj) || 0), 0);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b-2 border-slate-200 text-xs uppercase tracking-widest text-slate-500">
            <th className="text-left py-3 px-2 font-bold">Alt Kırılım</th>
            <th className="text-right py-3 px-2 font-bold">Birim</th>
            <th className="text-right py-3 px-2 font-bold min-w-[180px]">Planlanan</th>
            <th className="text-right py-3 px-2 font-bold min-w-[150px]">Yapılan</th>
            <th className="text-right py-3 px-2 font-bold">Kalan</th>
            <th className="text-right py-3 px-2 font-bold min-w-[140px]" title="Alt kırılım ağırlığı — tüm gruplarda aynıdır">Alt Pursantaj</th>
            <th className="text-right py-3 px-2 font-bold min-w-[160px]">İlerleme</th>
            {isAdmin && <th className="text-right py-3 px-2 font-bold w-20"></th>}
          </tr>
        </thead>
        <tbody>
          {subs.map((s) => (
            <tr key={s.key} className="border-b border-slate-100" data-testid={`sub-row-${s.key}${groupKey ? "-" + groupKey : ""}`}>
              <td className="py-3 px-2 font-semibold text-slate-900">
                <div className="flex items-center gap-2">
                  {s.name}
                  {s.is_custom && (
                    <span className="text-[9px] font-bold uppercase tracking-widest bg-sky-100 text-sky-700 border border-sky-200 px-1.5 py-0.5 rounded" title="Özel">
                      <Sparkles size={9} className="inline mr-0.5 -mt-0.5" /> Özel
                    </span>
                  )}
                </div>
              </td>
              <td className="py-3 px-2 text-right text-slate-600 font-mono-num">{s.unit}</td>
              <td className="py-3 px-2 text-right">
                <PlannedCell itemKey={item.key} subKey={s.key} groupKey={groupKey} planned={s.planned} canEdit={isAdmin} onSaved={load} />
              </td>
              <td className="py-3 px-2 text-right">
                <button
                  type="button"
                  onClick={() => onOpenEntries(s, groupKey)}
                  className="inline-flex items-center gap-1.5 text-green-700 hover:text-green-800 hover:bg-green-50 px-2 py-1 rounded font-mono-num font-bold group"
                  title="Bu alt kırılıma ait kayıtları görüntüle & düzenle"
                  data-testid={`open-entries-${s.key}${groupKey ? "-" + groupKey : ""}`}
                >
                  {formatNumber(s.done)}
                  <ListChecks size={13} className="opacity-40 group-hover:opacity-100" />
                </button>
              </td>
              <td className="py-3 px-2 text-right font-mono-num text-sky-700">{formatNumber(s.remaining)}</td>
              <td className="py-3 px-2 text-right">
                <SubPursantajCell itemKey={item.key} subKey={s.key} pursantaj={s.sub_pursantaj || 0} canEdit={isAdmin} onSaved={load} />
              </td>
              <td className="py-3 px-2 min-w-[160px]">
                <div className="flex items-center gap-3">
                  <ProgressBar pct={s.progress} />
                  <span className="font-mono-num font-bold text-xs w-14 text-right" style={{ color: progressColor(s.progress) }}>
                    %{s.progress.toFixed(1)}
                  </span>
                </div>
              </td>
              {isAdmin && (
                <td className="py-3 px-2 text-right">
                  <div className="flex items-center justify-end gap-0.5">
                    <button type="button" onClick={() => onEditSub(s)} className="text-slate-500 hover:text-sky-600 hover:bg-sky-50 p-1.5 rounded" title="Düzenle" data-testid={`edit-sub-${s.key}`}>
                      <Pencil size={13} />
                    </button>
                    <button type="button" onClick={() => onDeleteSub(s)} className="text-red-600 hover:bg-red-50 p-1.5 rounded" title="Sil" data-testid={`delete-sub-${s.key}`}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              )}
            </tr>
          ))}
          {subs.length === 0 && (
            <tr>
              <td colSpan={isAdmin ? 8 : 7} className="text-center text-sm text-slate-500 py-8">
                Henüz alt kırılım tanımlanmamış.
              </td>
            </tr>
          )}
        </tbody>
        {subs.length > 0 && (
          <tfoot>
            <tr className="border-t-2 border-slate-200 bg-slate-50/60">
              <td colSpan={5} className="py-2 px-2 text-right text-xs font-bold uppercase tracking-widest text-slate-500">
                Toplam Alt Pursantaj
              </td>
              <td className="py-2 px-2 text-right">
                <span
                  className={`inline-flex items-center gap-1 font-mono-num font-black text-xs px-2 py-1 rounded-full border ${
                    Math.abs(totalSubPurs - 100) < 0.1
                      ? "bg-green-50 text-green-800 border-green-300"
                      : totalSubPurs > 0
                      ? "bg-amber-50 text-amber-800 border-amber-300"
                      : "bg-slate-50 text-slate-500 border-slate-200"
                  }`}
                  data-testid={`sub-pursantaj-total${groupKey ? "-" + groupKey : ""}`}
                >
                  %{totalSubPurs.toFixed(1)}
                  {Math.abs(totalSubPurs - 100) < 0.1 ? " ✓" : totalSubPurs > 0 ? " (öneri: %100)" : ""}
                </span>
              </td>
              <td />
              {isAdmin && <td />}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

export default function ItemDetailPage() {
  const { itemKey } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [deleteSub, setDeleteSub] = useState(null);
  const [deleteGroup, setDeleteGroup] = useState(null);
  const [deletedSubs, setDeletedSubs] = useState([]);
  const [entriesFor, setEntriesFor] = useState(null); // {sub, groupKey}
  const preservedScrollRef = useRef(null);

  const load = useCallback(async (preserveScroll = false) => {
    if (preserveScroll) {
      preservedScrollRef.current = window.scrollY;
    }
    try {
      const { data } = await api.get("/dashboard/summary");
      const found = (data.items || []).find((x) => x.key === itemKey);
      if (!found) { toast.error("İmalat kalemi bulunamadı"); navigate("/"); return; }
      setItem(found);
    } catch { toast.error("Veriler yüklenemedi"); }
    finally { setLoading(false); }
  }, [itemKey, navigate]);

  // Restore scroll position after state updates settle (fixes scroll-to-top bug when toggling ano stages)
  useLayoutEffect(() => {
    if (preservedScrollRef.current !== null) {
      const y = preservedScrollRef.current;
      preservedScrollRef.current = null;
      window.scrollTo(0, y);
    }
  }, [item]);

  const loadDeleted = useCallback(async () => {
    if (user?.role !== "admin") return;
    try {
      const { data } = await api.get(`/items/deleted-sub-items/${itemKey}`);
      setDeletedSubs(data);
    } catch (err) {
      console.error("Silinmiş alt kırılımlar yüklenemedi", err);
    }
  }, [itemKey, user?.role]);

  const restoreSub = async (key) => {
    try {
      await api.post(`/items/sub-items/${itemKey}/${key}/restore`);
      toast.success("Alt kırılım geri yüklendi");
      load();
      loadDeleted();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => { load(); loadDeleted(); }, [load, loadDeleted]);

  if (loading || !item) {
    return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="animate-spin text-sky-500" size={32} /></div>;
  }

  const isAdmin = user?.role === "admin";
  const canEdit = user?.role === "admin" || user?.role === "editor";
  const groups = item.groups || [];
  const hasGroups = groups.length > 0;
  const isDere = item.key === "dere" || item.kind === "dere_ano";

  const chartData = item.sub_items.map((s) => ({
    name: s.name.length > 20 ? s.name.slice(0, 18) + "…" : s.name,
    fullName: s.name,
    progress: s.progress,
    color: progressColor(s.progress),
    planned: s.planned,
    done: s.done,
    unit: s.unit,
  }));

  return (
    <div className="space-y-6" data-testid="item-detail-page">
      <Button variant="outline" onClick={() => navigate("/")} className="border-2 border-slate-300 font-semibold" data-testid="back-to-dashboard">
        <ArrowLeft size={16} className="mr-2" /> Panele Dön
      </Button>

      <div className="site-card p-4 sm:p-6 rounded-lg">
        <div className="flex items-start justify-between flex-wrap gap-3 sm:gap-4">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-sky-600 mb-2">
              {isDere ? "Ano Bazlı Takip (Uzunluk Ağırlıklı)" : hasGroups ? "Grup Bazlı Detay" : "Alt Kırılım Detayları"}
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 tracking-tight font-display break-words" data-testid="item-detail-name">{item.name}</h1>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Genel İlerleme</div>
            <div className="font-display font-black text-3xl sm:text-4xl md:text-5xl tabular-nums" style={{ color: progressColor(item.progress) }}>%{item.progress.toFixed(1)}</div>
          </div>
        </div>
        <div className="mt-5 sm:mt-6"><ProgressBar pct={item.progress} /></div>
        {(isDere || !hasGroups) && (
          <div className="grid grid-cols-3 gap-2 sm:gap-4 mt-5 sm:mt-6">
            <div className="min-w-0"><div className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-slate-500 truncate">{isDere ? "Toplam Uzunluk" : "Toplam Planlanan"}</div><div className="font-mono-num font-black text-base sm:text-xl md:text-2xl text-slate-900 truncate">{formatNumber(item.planned)}{isDere ? " m" : ""}</div></div>
            <div className="min-w-0"><div className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-slate-500 truncate">{isDere ? "Tamamlanan" : "Toplam Yapılan"}</div><div className="font-mono-num font-black text-base sm:text-xl md:text-2xl text-green-700 truncate">{formatNumber(item.done)}{isDere ? " m" : ""}</div></div>
            <div className="min-w-0"><div className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-slate-500 truncate">{isDere ? "Kalan" : "Toplam Kalan"}</div><div className="font-mono-num font-black text-base sm:text-xl md:text-2xl text-sky-700 truncate">{formatNumber(item.remaining)}{isDere ? " m" : ""}</div></div>
          </div>
        )}
        {hasGroups && !isDere && (
          <div className="mt-5 sm:mt-6 text-[11px] sm:text-xs text-slate-500 flex items-center gap-1.5 sm:gap-2 flex-wrap">
            <span className="font-bold uppercase tracking-widest">Hesaplama:</span>
            <span>Grup % = Alt kırılım × Alt pursantaj ağırlıklı ortalaması</span>
            <span className="text-slate-300 hidden sm:inline">·</span>
            <span>İmalat % = Grupların basit ortalaması ({groups.length} grup)</span>
          </div>
        )}
        {!hasGroups && !isDere && (item.sub_items || []).some((s) => (s.sub_pursantaj || 0) > 0) && (
          <div className="mt-5 sm:mt-6 text-[11px] sm:text-xs text-slate-500 flex items-center gap-1.5 sm:gap-2 flex-wrap">
            <span className="font-bold uppercase tracking-widest">Hesaplama:</span>
            <span>İmalat % = Alt kırılım × Alt pursantaj ağırlıklı ortalaması</span>
          </div>
        )}
      </div>

      {isDere ? (
        <>
          <DereStagePursantajPanel
            initial={item.stage_pursantaj || { kazi: 0, radye: 0, perde: 0, tabliye: 0 }}
            totalPursantaj={item.dere_total_pursantaj || 0}
            isAdmin={isAdmin}
            onSaved={() => load(true)}
          />
          <DereAnoView
            item={item}
            canEdit={canEdit}
            isAdmin={isAdmin}
            onGroupAdd={() => setShowAddGroup(true)}
            onGroupEdit={setEditingGroup}
            onGroupDelete={setDeleteGroup}
            onChanged={() => load(true)}
          />
          {/* Ek Alt Kırılımlar (Servis Yolu vb.) — Dere için ek metraj takibi */}
          <div className="site-card p-6 rounded-lg" data-testid="dere-extra-subs">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <div>
                <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight font-display flex items-center gap-2">
                  <ListChecks size={20} className="text-sky-600" /> Ek Alt Kırılımlar
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  Dere'ye bağlı ekstra iş kalemleri (örn: Servis Yolu, İzolasyon, Menfez). Her alt kırılıma Alt Pursantaj tanımlayabilir, günlük metraj kaydı girebilirsiniz.
                </p>
              </div>
              {isAdmin && (
                <Button className="bg-sky-500 hover:bg-sky-600 text-white h-10 font-bold" onClick={() => setShowAddModal(true)} data-testid="add-dere-sub-item-button">
                  <Plus size={14} className="mr-1.5" /> Yeni Alt Kırılım
                </Button>
              )}
            </div>
            {(item.sub_items || []).length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-md">
                <div className="text-slate-500 mb-2 text-sm">Ek alt kırılım tanımlanmamış.</div>
                {isAdmin && (
                  <Button variant="outline" className="border-2 border-sky-300 text-sky-700 font-bold" onClick={() => setShowAddModal(true)}>
                    <Plus size={14} className="mr-1.5" /> İlk Alt Kırılımı Ekle
                  </Button>
                )}
              </div>
            ) : (
              <Accordion type="multiple" className="space-y-2">
                {(item.groups || []).map((grp) => (
                  <AccordionItem key={grp.key} value={grp.key} className="border border-slate-200 rounded-lg" data-testid={`dere-subs-group-${grp.key}`}>
                    <AccordionTrigger className="px-4 py-3 hover:no-underline">
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="w-9 h-9 rounded-md flex items-center justify-center bg-sky-100 text-sky-700 flex-shrink-0">
                          <Layers3 size={16} />
                        </div>
                        <div className="flex-1 text-left min-w-0">
                          <div className="font-black text-slate-900 truncate">{grp.name}</div>
                          <div className="text-xs text-slate-500 mt-0.5">{(grp.sub_items || []).length} ek alt kırılım</div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      <div className="pt-2 border-t border-slate-100">
                        <SubItemsTable item={item} subs={grp.sub_items || []} groupKey={grp.key} isAdmin={isAdmin} load={load} onEditSub={setEditingSub} onDeleteSub={setDeleteSub} onOpenEntries={(sub, gk) => setEntriesFor({ sub, groupKey: gk })} />
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        </>
      ) : hasGroups ? (
        /* --- Groups-first view: any item with groups --- */
        <>
          {/* Alt Kırılım Genel Özeti (across all groups) */}
          {item.sub_items && item.sub_items.length > 0 && (
            <div className="site-card p-6 rounded-lg" data-testid="subitem-summary-table">
              <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight font-display mb-4">
                Alt Kırılım Genel Özeti
                <span className="ml-2 text-xs font-bold text-slate-500 normal-case tracking-normal">(Tüm gruplar birleşik ilerleme %)</span>
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-slate-200 text-xs uppercase tracking-widest text-slate-500">
                      <th className="text-left py-2 px-2 font-bold">Alt Kırılım</th>
                      <th className="text-right py-2 px-2 font-bold">Birim</th>
                      <th className="text-right py-2 px-2 font-bold min-w-[220px]">İlerleme</th>
                    </tr>
                  </thead>
                  <tbody>
                    {item.sub_items.map((s) => (
                      <tr key={s.key} className="border-b border-slate-100" data-testid={`summary-sub-${s.key}`}>
                        <td className="py-2 px-2 font-semibold text-slate-900">
                          <div className="flex items-center gap-2">
                            {s.name}
                            {s.is_custom && (
                              <span className="text-[9px] font-bold uppercase tracking-widest bg-sky-100 text-sky-700 border border-sky-200 px-1.5 py-0.5 rounded">
                                <Sparkles size={9} className="inline mr-0.5 -mt-0.5" /> Özel
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-2 text-right text-slate-600 font-mono-num">{s.unit}</td>
                        <td className="py-2 px-2 min-w-[220px]">
                          <div className="flex items-center gap-3">
                            <ProgressBar pct={s.progress} />
                            <span className="font-mono-num font-bold text-xs w-14 text-right" style={{ color: progressColor(s.progress) }}>%{s.progress.toFixed(1)}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="site-card p-6 rounded-lg">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight font-display flex items-center gap-2">
                <Layers3 size={20} className="text-sky-600" /> Gruplar ({groups.length})
              </h2>
              <p className="text-xs text-slate-500 mt-1">Her grup için alt kırılım metrajlarını ayrı ayrı yönetebilirsiniz. Alt kırılım eklediğinizde tüm gruplara otomatik uygulanır.</p>
            </div>
            {isAdmin && (
              <div className="flex gap-2">
                <Button variant="outline" className="border-2 border-slate-300 h-10 font-semibold" onClick={() => setShowAddModal(true)} data-testid="add-sub-item-button">
                  <Plus size={14} className="mr-1.5" /> Alt Kırılım
                </Button>
                <Button className="bg-sky-500 hover:bg-sky-600 text-white h-10 font-bold" onClick={() => setShowAddGroup(true)} data-testid="add-group-button">
                  <Plus size={14} className="mr-1.5" /> Yeni Grup
                </Button>
              </div>
            )}
          </div>

          <Accordion type="multiple" className="space-y-2">
            {(item.groups || []).map((g) => {
              // find group with subs from item.groups populated by API (has sub_items)
              const grp = (item.groups || []).find((x) => x.key === g.key) || g;
              return (
                <AccordionItem key={grp.key} value={grp.key} className="border border-slate-200 rounded-lg" data-testid={`group-${grp.key}`}>
                  <AccordionTrigger className="px-3 sm:px-4 py-3 hover:no-underline">
                    <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
                      <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-md flex items-center justify-center bg-sky-100 text-sky-700 flex-shrink-0">
                        <Layers3 size={14} />
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="font-black text-slate-900 truncate text-sm sm:text-base">{grp.name}</div>
                        <div className="text-[11px] sm:text-xs text-slate-500 mt-0.5">{(grp.sub_items || []).length} alt kırılım</div>
                      </div>
                      <div className="text-right pr-1 sm:pr-2 flex-shrink-0">
                        <div className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-slate-500 hidden sm:block">İlerleme</div>
                        <div className="font-mono-num font-black text-base sm:text-lg" style={{ color: progressColor(grp.progress || 0) }}>%{(grp.progress || 0).toFixed(1)}</div>
                      </div>
                      {isAdmin && (
                        <div className="hidden sm:flex items-center gap-0.5 mr-2 flex-shrink-0">
                          <button type="button" onClick={(e) => { e.stopPropagation(); setEditingGroup(grp); }} className="p-1.5 text-slate-500 hover:text-sky-600 hover:bg-sky-50 rounded" title="Yeniden adlandır" data-testid={`edit-group-${grp.key}`}>
                            <Pencil size={13} />
                          </button>
                          <button type="button" onClick={(e) => { e.stopPropagation(); setDeleteGroup(grp); }} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Grubu sil" data-testid={`delete-group-${grp.key}`}>
                            <Trash2 size={13} />
                          </button>
                        </div>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="pt-2 border-t border-slate-100">
                      <SubItemsTable item={item} subs={grp.sub_items || []} groupKey={grp.key} isAdmin={isAdmin} load={load} onEditSub={setEditingSub} onDeleteSub={setDeleteSub} onOpenEntries={(sub, gk) => setEntriesFor({ sub, groupKey: gk })} />
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
        </>
      ) : (
        /* --- No groups: standard sub-items table (with option to start using groups) --- */
        <div className="site-card p-6 rounded-lg">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight font-display">Alt Kırılım Metrajları</h2>
              {isAdmin && <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-1"><Pencil size={12} className="text-sky-600" />Planlanan sütunundaki ikona tıklayarak düzenleyin</div>}
            </div>
            {isAdmin && (
              <div className="flex gap-2">
                <Button variant="outline" className="border-2 border-slate-300 h-10 font-semibold" onClick={() => setShowAddGroup(true)} data-testid="add-group-button">
                  <Plus size={14} className="mr-1.5" /> Grup Oluştur
                </Button>
                <Button className="bg-sky-500 hover:bg-sky-600 text-white font-bold h-10" onClick={() => setShowAddModal(true)} data-testid="add-sub-item-button">
                  <Plus size={16} className="mr-2" /> Yeni Alt Kırılım
                </Button>
              </div>
            )}
          </div>
          {isAdmin && (
            <div className="mb-4 text-xs bg-sky-50 border border-sky-200 text-sky-800 rounded-md px-3 py-2 flex items-start gap-2">
              <Layers3 size={14} className="mt-0.5 flex-shrink-0" />
              <span>İpucu: Bu imalatı bölgelere/hatlara ayırmak isterseniz üstteki <b>Grup Oluştur</b> butonunu kullanın (örn: Hat 1, Hat 2, Gökdere). Mevcut kayıtlar otomatik olarak <b>"Genel"</b> grubuna taşınır.</span>
            </div>
          )}
          <SubItemsTable item={item} subs={item.sub_items} groupKey={null} isAdmin={isAdmin} load={load} onEditSub={setEditingSub} onDeleteSub={setDeleteSub} onOpenEntries={(sub, gk) => setEntriesFor({ sub, groupKey: gk })} />
        </div>
      )}

      {/* Deleted (soft-deleted) built-ins — restore panel */}
      {isAdmin && deletedSubs.length > 0 && (
        <div className="site-card p-6 rounded-lg border-dashed" data-testid="deleted-subs-panel">
          <div className="flex items-center gap-2 mb-3">
            <Trash2 size={16} className="text-slate-400" />
            <h3 className="text-sm font-black uppercase tracking-widest text-slate-700">Silinmiş Alt Kırılımlar ({deletedSubs.length})</h3>
          </div>
          <p className="text-xs text-slate-500 mb-4">
            Yerleşik alt kırılımlar yumuşak silindi — geri yükleyebilirsiniz. Not: silinmiş dönemin planlanan/kayıtları temizlendi, geri yüklendiğinde tekrar sıfırdan başlar.
          </p>
          <div className="flex flex-wrap gap-2">
            {deletedSubs.map((d) => (
              <div key={d.key} className="inline-flex items-center gap-2 bg-slate-100 border border-slate-200 rounded-full pl-3 pr-1 py-1" data-testid={`deleted-sub-${d.key}`}>
                <span className="text-sm text-slate-700 font-semibold">{d.name}</span>
                <span className="text-xs text-slate-500">({d.unit})</span>
                <button
                  type="button"
                  onClick={() => restoreSub(d.key)}
                  className="ml-1 flex items-center gap-1 bg-white hover:bg-sky-50 text-sky-700 border border-sky-300 rounded-full px-2 py-0.5 text-xs font-bold"
                  data-testid={`restore-sub-${d.key}`}
                >
                  <RotateCcw size={11} /> Geri yükle
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bar chart — aggregate view across all sub-items */}
      {!isDere && chartData.length > 0 && (
        <div className="site-card p-6 rounded-lg">
          <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight mb-4 font-display">
            {hasGroups ? "Alt Kırılım Toplam Grafiği (Tüm Gruplar)" : "Alt Kırılım Grafiği"}
          </h2>
          <div className="w-full h-80">
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="name" angle={-25} textAnchor="end" interval={0} tick={{ fontSize: 11, fill: "#475569" }} />
                <YAxis domain={[0, 100]} tickFormatter={(v) => `%${v}`} tick={{ fontSize: 11, fill: "#475569" }} />
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0].payload;
                  return (
                    <div className="bg-white border-2 border-slate-300 rounded-md p-3 shadow-lg">
                      <div className="font-bold text-slate-900">{d.fullName}</div>
                      <div className="text-xs text-slate-500 mt-1">{formatNumber(d.done)} / {formatNumber(d.planned)} {d.unit}</div>
                      <div className="text-sm mt-1" style={{ color: d.color }}>%{d.progress.toFixed(1)} tamamlandı</div>
                    </div>
                  );
                }} />
                <Bar dataKey="progress" radius={[6, 6, 0, 0]}>{chartData.map((d, i) => (<Cell key={i} fill={d.color} />))}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Modals */}
      <EntriesModal
        entriesFor={entriesFor}
        item={item}
        canEdit={user?.role === "admin" || user?.role === "editor"}
        onClose={() => setEntriesFor(null)}
        onChanged={load}
      />
      <SubItemDialog mode="create" open={showAddModal} onClose={() => setShowAddModal(false)} itemKey={item.key} itemName={item.name} onSaved={() => { setShowAddModal(false); load(); }} />
      <SubItemDialog mode="edit" open={!!editingSub} onClose={() => setEditingSub(null)} itemKey={item.key} itemName={item.name} sub={editingSub} onSaved={() => { setEditingSub(null); load(); }} />
      <GroupDialog mode="create" open={showAddGroup} onClose={() => setShowAddGroup(false)} itemKey={item.key} itemName={item.name} onSaved={() => { setShowAddGroup(false); load(); }} />
      <GroupDialog mode="edit" open={!!editingGroup} onClose={() => setEditingGroup(null)} itemKey={item.key} itemName={item.name} group={editingGroup} onSaved={() => { setEditingGroup(null); load(); }} />

      <AlertDialog open={!!deleteSub} onOpenChange={(o) => !o && setDeleteSub(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Alt kırılımı sil?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-bold">{deleteSub?.name}</span> alt kırılımı silinecek. Bağlı planlanan metraj ve tüm günlük kayıtlar (tüm gruplarda) da silinir.
              {deleteSub && !deleteSub.is_custom && (
                <span className="block mt-2 text-slate-500 text-xs">
                  Bu bir yerleşik alt kırılım — istediğinizde <b>Silinmiş Alt Kırılımlar</b> panelinden geri yükleyebilirsiniz.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={async () => {
              try { await api.delete(`/items/sub-items/${item.key}/${deleteSub.key}`); toast.success("Silindi"); setDeleteSub(null); load(); loadDeleted(); }
              catch (err) { toast.error(formatApiError(err)); }
            }} data-testid="delete-sub-confirm">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteGroup} onOpenChange={(o) => !o && setDeleteGroup(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Grubu sil?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-bold">{deleteGroup?.name}</span> grubuna ait tüm planlanan metrajlar ve günlük kayıtlar silinecek. Geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={async () => {
              try { await api.delete(`/groups/${item.key}/${deleteGroup.key}`); toast.success("Grup silindi"); setDeleteGroup(null); load(); }
              catch (err) { toast.error(formatApiError(err)); }
            }} data-testid="delete-group-confirm">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SubItemDialog({ mode, open, onClose, itemKey, itemName, sub, onSaved }) {
  const [name, setName] = useState("");
  const [unit, setUnit] = useState("adet");
  const [customUnit, setCustomUnit] = useState("");
  const [useCustomUnit, setUseCustomUnit] = useState(false);
  const [saving, setSaving] = useState(false);
  const isEdit = mode === "edit";

  useEffect(() => {
    if (open) {
      if (isEdit && sub) {
        setName(sub.name);
        if (UNIT_OPTIONS.includes(sub.unit)) { setUnit(sub.unit); setUseCustomUnit(false); setCustomUnit(""); }
        else { setUnit("adet"); setUseCustomUnit(true); setCustomUnit(sub.unit); }
      } else {
        setName(""); setUnit("adet"); setCustomUnit(""); setUseCustomUnit(false);
      }
    }
  }, [open, isEdit, sub]);

  const submit = async () => {
    const finalUnit = useCustomUnit ? customUnit.trim() : unit;
    if (!name.trim() || !finalUnit) { toast.error("Ad ve birim zorunludur"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/items/sub-items/${itemKey}/${sub.key}`, { name: name.trim(), unit: finalUnit });
        toast.success("Alt kırılım güncellendi");
      } else {
        await api.post("/items/sub-items", { item_key: itemKey, name: name.trim(), unit: finalUnit });
        toast.success("Alt kırılım eklendi — tüm gruplara otomatik uygulanır");
      }
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={isEdit ? "edit-sub-item-dialog" : "add-sub-item-dialog"}>
        <DialogHeader>
          <DialogTitle className="text-xl font-black font-display">{isEdit ? "Alt Kırılımı Düzenle" : "Yeni Alt Kırılım Ekle"}</DialogTitle>
          <DialogDescription>
            <span className="font-bold text-slate-700">{itemName}</span>
            {isEdit ? " altındaki bu alt kırılımı düzenleyin." : " imalat kalemine yeni alt kırılım tanımlayın. (Gruplar varsa hepsine otomatik uygulanır.)"}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <Label className="font-semibold text-sm text-slate-700">Ad *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Örn: Kazı" className="mt-1.5 h-11 border-2 border-slate-300" autoFocus data-testid="sub-name-input" />
          </div>
          <div>
            <Label className="font-semibold text-sm text-slate-700">Birim *</Label>
            {!useCustomUnit ? (
              <div className="flex gap-2 mt-1.5">
                <Select value={unit} onValueChange={setUnit}>
                  <SelectTrigger className="h-11 border-2 border-slate-300 flex-1" data-testid="sub-unit-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{UNIT_OPTIONS.map((u) => (<SelectItem key={u} value={u}>{u}</SelectItem>))}</SelectContent>
                </Select>
                <Button variant="outline" className="h-11 border-2 border-slate-300 font-semibold text-xs" onClick={() => setUseCustomUnit(true)} type="button">Diğer…</Button>
              </div>
            ) : (
              <div className="flex gap-2 mt-1.5">
                <Input value={customUnit} onChange={(e) => setCustomUnit(e.target.value)} placeholder="Örn: takım" className="h-11 border-2 border-slate-300 flex-1 font-mono-num" />
                <Button variant="outline" className="h-11 border-2 border-slate-300 font-semibold text-xs" onClick={() => { setUseCustomUnit(false); setCustomUnit(""); }} type="button">Listeden</Button>
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button className="bg-sky-500 hover:bg-sky-600 text-white font-bold" onClick={submit} disabled={saving} data-testid="sub-save">
            {saving ? <Loader2 className="animate-spin" size={16} /> : (isEdit ? "Kaydet" : "Ekle")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GroupDialog({ mode, open, onClose, itemKey, itemName, group, onSaved }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const isEdit = mode === "edit";

  useEffect(() => {
    if (open) setName(isEdit && group ? group.name : "");
  }, [open, isEdit, group]);

  const submit = async () => {
    if (!name.trim()) { toast.error("Grup adı zorunlu"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/groups/${itemKey}/${group.key}`, { name: name.trim() });
        toast.success("Grup güncellendi");
      } else {
        await api.post("/groups", { item_key: itemKey, name: name.trim() });
        toast.success("Grup eklendi");
      }
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={isEdit ? "edit-group-dialog" : "add-group-dialog"}>
        <DialogHeader>
          <DialogTitle className="text-xl font-black font-display">{isEdit ? "Grubu Yeniden Adlandır" : "Yeni Grup Ekle"}</DialogTitle>
          <DialogDescription>
            <span className="font-bold text-slate-700">{itemName}</span>
            {isEdit ? " altındaki bu grubu yeniden adlandırın." : " altında yeni bir grup oluşturun. Örn: Hat 1, Hat 2, Gökdere, Çataldere, Yol-40 vb."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <Label className="font-semibold text-sm text-slate-700">Grup Adı *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Örn: Hat 1" className="mt-1.5 h-11 border-2 border-slate-300" autoFocus data-testid="group-name-input" />
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button className="bg-sky-500 hover:bg-sky-600 text-white font-bold" onClick={submit} disabled={saving} data-testid="group-save">
            {saving ? <Loader2 className="animate-spin" size={16} /> : (isEdit ? "Kaydet" : "Ekle")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EntriesModal({ entriesFor, item, canEdit, onClose, onChanged }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const sub = entriesFor?.sub;
  const groupKey = entriesFor?.groupKey;
  const open = !!entriesFor;

  const fetchEntries = async () => {
    if (!open) return;
    setLoading(true);
    try {
      const params = { item_key: item.key, sub_item_key: sub.key, limit: 1000 };
      if (groupKey) params.group_key = groupKey;
      const { data } = await api.get("/entries", { params });
      setEntries(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEntries(); /* eslint-disable-next-line */ }, [open, sub?.key, groupKey]);

  if (!open) return null;

  const startEdit = (e) => {
    setEditingId(e.id);
    setEditDraft({ date: e.date, quantity: String(e.quantity), location: e.location || "", note: e.note || "" });
  };
  const cancelEdit = () => { setEditingId(null); setEditDraft({}); };

  const saveEdit = async (id) => {
    if (!editDraft.quantity || parseFloat(editDraft.quantity) <= 0) {
      toast.error("Miktar 0'dan büyük olmalı");
      return;
    }
    setSavingId(id);
    try {
      await api.put(`/entries/${id}`, {
        date: editDraft.date,
        quantity: parseFloat(editDraft.quantity),
        location: editDraft.location,
        note: editDraft.note,
      });
      toast.success("Kayıt güncellendi");
      cancelEdit();
      fetchEntries();
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingId(null);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/entries/${deleteId}`);
      toast.success("Kayıt silindi");
      setDeleteId(null);
      fetchEntries();
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const total = entries.reduce((s, e) => s + e.quantity, 0);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="entries-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-black font-display flex items-center gap-2">
            <ListChecks size={20} className="text-sky-600" />
            Günlük Kayıtlar
          </DialogTitle>
          <DialogDescription>
            <span className="font-bold text-slate-700">{item.name}</span>
            {groupKey && <span className="text-sky-700"> · {(item.groups || []).find((g) => g.key === groupKey)?.name}</span>}
            <span> — {sub?.name}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between text-sm bg-slate-50 border border-slate-200 rounded-md px-4 py-2 mt-1">
          <div className="text-slate-600">Toplam kayıt: <span className="font-bold text-slate-900">{entries.length}</span></div>
          <div className="text-slate-600">Toplam: <span className="font-mono-num font-black text-green-700">{formatNumber(total)} {sub?.unit}</span></div>
        </div>

        <div className="max-h-[55vh] overflow-y-auto -mx-6 px-6 mt-2">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-sky-500" size={28} /></div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12 text-slate-500 text-sm" data-testid="entries-empty">
              Bu alt kırılıma ait kayıt bulunmuyor. Veri Girişi sayfasından ekleyebilirsiniz.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b-2 border-slate-200 text-xs uppercase tracking-widest text-slate-500">
                  <th className="text-left py-2 px-2 font-bold">Tarih</th>
                  <th className="text-right py-2 px-2 font-bold">Miktar</th>
                  <th className="text-left py-2 px-2 font-bold">Lokasyon</th>
                  <th className="text-left py-2 px-2 font-bold">Not</th>
                  <th className="text-left py-2 px-2 font-bold">Kaydeden</th>
                  {canEdit && <th className="text-right py-2 px-2 font-bold w-24"></th>}
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const isEditing = editingId === e.id;
                  return (
                    <tr key={e.id} className="border-b border-slate-100" data-testid={`entry-row-${e.id}`}>
                      <td className="py-2 px-2">
                        {isEditing ? (
                          <Input type="date" value={editDraft.date} onChange={(ev) => setEditDraft({ ...editDraft, date: ev.target.value })} className="h-9 border-2 border-sky-300 font-mono-num" data-testid="inline-edit-date" />
                        ) : (
                          <span className="font-mono-num">{e.date}</span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right">
                        {isEditing ? (
                          <Input type="number" step="0.01" value={editDraft.quantity} onChange={(ev) => setEditDraft({ ...editDraft, quantity: ev.target.value })} className="h-9 border-2 border-sky-300 font-mono-num text-right w-28 ml-auto" data-testid="inline-edit-quantity" />
                        ) : (
                          <span className="font-mono-num font-bold text-green-700">{formatNumber(e.quantity)}</span>
                        )}
                      </td>
                      <td className="py-2 px-2">
                        {isEditing ? (
                          <Input value={editDraft.location} onChange={(ev) => setEditDraft({ ...editDraft, location: ev.target.value })} className="h-9 border-2 border-sky-300" placeholder="—" />
                        ) : (
                          <span className="text-slate-600">{e.location || "—"}</span>
                        )}
                      </td>
                      <td className="py-2 px-2">
                        {isEditing ? (
                          <Input value={editDraft.note} onChange={(ev) => setEditDraft({ ...editDraft, note: ev.target.value })} className="h-9 border-2 border-sky-300" placeholder="—" />
                        ) : (
                          <span className="text-slate-600 line-clamp-1 max-w-[180px]">{e.note || "—"}</span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-xs text-slate-500">{e.created_by_name}</td>
                      {canEdit && (
                        <td className="py-2 px-2 text-right">
                          {isEditing ? (
                            <div className="flex items-center justify-end gap-0.5">
                              <button onClick={() => saveEdit(e.id)} disabled={savingId === e.id} className="p-1.5 text-green-700 hover:bg-green-50 rounded" title="Kaydet" data-testid={`inline-save-${e.id}`}>
                                {savingId === e.id ? <Loader2 className="animate-spin" size={13} /> : <Check size={13} />}
                              </button>
                              <button onClick={cancelEdit} className="p-1.5 text-red-700 hover:bg-red-50 rounded" title="İptal">
                                <X size={13} />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-end gap-0.5">
                              <button onClick={() => startEdit(e)} className="p-1.5 text-slate-500 hover:text-sky-600 hover:bg-sky-50 rounded" title="Düzenle" data-testid={`inline-edit-${e.id}`}>
                                <Pencil size={13} />
                              </button>
                              <button onClick={() => setDeleteId(e.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Sil" data-testid={`inline-delete-${e.id}`}>
                                <Trash2 size={13} />
                              </button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Kapat</Button>
        </DialogFooter>

        <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Kaydı sil?</AlertDialogTitle>
              <AlertDialogDescription>Bu günlük metraj kaydı kalıcı olarak silinecek.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>İptal</AlertDialogCancel>
              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={confirmDelete}>Sil</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
